import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { CloseCircleOutline, useToast, CheckOutline } from '@aslanpay/portal-common-lib';

import useCreateTransactionNote from 'app/hooks/useCreateTransactionNote';

import { createTransactionNoteSchema } from 'app/utils/schemas';

import { CreateTransactionNoteFormFields, CreateTransactionNoteModalProps } from './types';

function CreateTransactionNoteModal({
  selectedIds,
  isOpen,
  onClose,
  onCreate,
}: CreateTransactionNoteModalProps) {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateTransactionNoteFormFields>({
    mode: 'onBlur',
    defaultValues: { note: '' },
    resolver: yupResolver(createTransactionNoteSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: createTransactionNote, isLoading: isCreatingTransactionNote } =
    useCreateTransactionNote({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Success',
          description: `You have successfully created a transaction note`,
        });

        handleClose();
        onCreate();
      },
    });

  const handleCreateTransactionNote = (data: CreateTransactionNoteFormFields) => {
    createTransactionNote({ transactionIds: selectedIds, note: data.note });
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Add a note</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close add note"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="20px">
            <form
              id="create-transaction-note-form"
              onSubmit={handleSubmit(handleCreateTransactionNote)}
              noValidate
            >
              <FormControl isInvalid={!!errors?.note?.message}>
                <FormLabel>Note</FormLabel>
                <Textarea
                  placeholder="Please type something to be attached to the transacion(s)."
                  {...register('note')}
                />
                <FormErrorMessage>{errors?.note?.message}</FormErrorMessage>
              </FormControl>
            </form>
            <Text color="brand.mutedPurple" textStyle="body">
              Please note, anything you add here can’t be edited once you have entered it.
            </Text>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form="create-transaction-note-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isCreatingTransactionNote}
            isDisabled={!isValid}
          >
            Add note
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateTransactionNoteModal;
